import { useCallback, useEffect, useRef } from 'react'
import type mapboxgl from 'mapbox-gl'
import { autoUpdate, offset, shift, useFloating } from '@floating-ui/react-dom'

import {
  MAP_OFFER_CARD_FLOATING_EDGE_GAP,
  MAP_OFFER_CARD_FLOATING_TOP_OFFSET,
} from 'lib/maps/mapOfferCardFloating'

interface Options {
  map: mapboxgl.Map | null
  open: boolean
}

export default function useMapFloatingOfferCard({ map, open }: Options) {
  const { refs, floatingStyles, update } = useFloating({
    placement: 'bottom',
    strategy: 'absolute',
    middleware: [
      offset(({ placement }) => {
        // Reference point is the marker anchor (pin tip), so top placement
        // needs extra offset to clear the visible pin body.
        if (placement.startsWith('top')) return MAP_OFFER_CARD_FLOATING_TOP_OFFSET
        return MAP_OFFER_CARD_FLOATING_EDGE_GAP
      }),
      shift({
        padding: MAP_OFFER_CARD_FLOATING_EDGE_GAP,
        mainAxis: true,
        crossAxis: true,
        boundary: map?.getContainer() ?? 'clippingAncestors',
      }),
    ],
    open,
    whileElementsMounted: autoUpdate,
  })

  const floatingElementRef = useRef<HTMLDivElement | null>(null)
  const shouldRestoreDoubleClickZoomRef = useRef(false)

  const stopOfferCardDoubleClickPropagation = useCallback((event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const setFloatingElement = useCallback((node: HTMLDivElement | null) => {
    if (floatingElementRef.current) {
      floatingElementRef.current.removeEventListener('dblclick', stopOfferCardDoubleClickPropagation)
    }

    floatingElementRef.current = node
    refs.setFloating(node)

    if (node) {
      node.addEventListener('dblclick', stopOfferCardDoubleClickPropagation)
    }
  }, [refs, stopOfferCardDoubleClickPropagation])

  useEffect(() => {
    if (!map || !open) {
      return
    }

    map.on('move', update)

    return () => {
      map.off('move', update)
    }
  }, [map, open, update])

  const disableMapDoubleClickZoom = useCallback(() => {
    if (!map) {
      return
    }

    const isEnabled = map.doubleClickZoom.isEnabled?.() ?? true
    shouldRestoreDoubleClickZoomRef.current = isEnabled

    if (isEnabled) {
      map.doubleClickZoom.disable()
    }
  }, [map])

  const restoreMapDoubleClickZoom = useCallback(() => {
    if (!map || !shouldRestoreDoubleClickZoomRef.current) {
      return
    }

    map.doubleClickZoom.enable()
    shouldRestoreDoubleClickZoomRef.current = false
  }, [map])

  useEffect(() => {
    if (!open) {
      restoreMapDoubleClickZoom()
    }
  }, [open, restoreMapDoubleClickZoom])

  useEffect(() => () => {
    restoreMapDoubleClickZoom()

    if (floatingElementRef.current) {
      floatingElementRef.current.removeEventListener('dblclick', stopOfferCardDoubleClickPropagation)
    }
  }, [restoreMapDoubleClickZoom, stopOfferCardDoubleClickPropagation])

  return {
    setReference: refs.setReference,
    setFloatingElement,
    floatingStyles,
    disableMapDoubleClickZoom,
    restoreMapDoubleClickZoom,
  }
}
